import { categoryIdZodSchema } from "@@/shared/categories/category-id";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import { categoryZodObject } from "./category";

const categoryUpdateZodObject = categoryZodObject
    .omit({
        _createdAt: true,
        _id: true,
        _type: true,
        _status: true,
    })
    .partial()
    .merge(
        z.object({
            _id: categoryIdZodSchema,
        }),
    );

type CategoryUpdate = z.infer<typeof categoryUpdateZodObject>;
const categoryAdditionZodObject = categoryZodObject.omit({
    _createdAt: true,
    _type: true,
    _status: true,
});
type CategoryAddition = z.infer<typeof categoryAdditionZodObject>;

const setCategoriesCommandZodObject = z
    .object({
        _id: idZodSchema,
        _type: z.literal("SET_CATEGORIES_COMMAND"),
        _createdAt: isoAndUnixZodSchema,
        updates: categoryUpdateZodObject.array(),
        removals: categoryIdZodSchema.array(),
        additions: categoryAdditionZodObject.array(),
    })
    .refine(value => {
        if (!value.updates?.length && value.removals?.length) {
            return false;
        }
        return true;
    }, "Set categories command contains no changes");

type SetCategoriesCommand = z.infer<typeof setCategoriesCommandZodObject>;
const setCategoriesCommandZodScheme =
    setCategoriesCommandZodObject as z.ZodSchema<SetCategoriesCommand>;

type FactoryParams = Except<
    SetCategoriesCommand,
    "_id" | "_type" | "_createdAt"
>;
const setCategoriesCommandFactory = (params: FactoryParams) => {
    const command: SetCategoriesCommand = {
        ...params,
        _id: generateId({ prefix: "set_categories_command__" }),
        _type: "SET_CATEGORIES_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

export {
    categoryAdditionZodObject,
    categoryUpdateZodObject,
    setCategoriesCommandFactory,
    setCategoriesCommandZodObject,
    setCategoriesCommandZodScheme,
};
export type {
    CategoryAddition,
    CategoryUpdate,
    SetCategoriesCommand,
    FactoryParams as SetCategoriesCommandFactoryParams,
};
